/**
 * Support / Notifications pilot UI module
 * ---------------------------------------
 *  - SupportEnquiryForm   (with WhatsApp opt-in)
 *  - NotificationPreferencesPanel
 *  - NotificationHistoryPanel
 *
 * All submissions go through geo-api via notificationService / investorService.
 */
import React, { useEffect, useState } from 'react';
import { LifeBuoy, Bell, MessageCircle, Send, Loader2, CheckCircle2 } from 'lucide-react';
import {
  notificationService,
  type NotificationPreferences,
} from '@/services/notificationService';
import type { NotificationItem } from '@/services/investorService';
import { Button, Card, CardContent, Input, Loading, EmptyState } from '@/components/eds';

const inputCls = 'w-full px-3 py-2.5 border border-eds-border bg-eds-surface-2 text-eds-fg placeholder:text-eds-muted-fg rounded-eds-md text-sm focus:outline-none focus:ring-2 focus:ring-eds-emerald/40';
const submitCls = 'inline-flex items-center gap-2';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-eds-fg uppercase tracking-wider">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

/* ───────── Support enquiry form ───────── */

export const SupportEnquiryForm: React.FC = () => {
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [whatsappOptIn, setWhatsappOptIn] = useState(false);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const fd = new FormData(formEl);
    setBusy(true);
    setError(null);
    try {
      // WP2.2 I-09 / data-honesty: success is shown ONLY when geo-api confirms
      // it. A non-2xx result surfaces a governed, request-ID-safe error — never
      // a fake "Submitted" state, never a raw backend object.
      const res = await notificationService.submitEnquiry({
        subject: String(fd.get('subject') || ''),
        category: String(fd.get('category') || 'general'),
        message: String(fd.get('message') || ''),
        whatsappOptIn,
        whatsappNumber: whatsappOptIn ? String(fd.get('whatsapp') || '') : undefined,
      });
      if (res.error) {
        setError(
          res.status === 403
            ? 'You do not currently have permission to submit an enquiry. Please contact GIISDEC support.'
            : 'Your enquiry could not be submitted right now. Please try again shortly, or email info@giisdec.com.'
        );
        return;
      }
      setDone(true);
      formEl.reset();
      setWhatsappOptIn(false);
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-2 flex items-center gap-2 text-xs text-eds-muted-fg">
        <LifeBuoy className="w-4 h-4 text-eds-emerald-2" />
        Enquiries are routed through geo-api to the GIISDEC support desk.
      </div>
      <Field label="Subject"><input name="subject" className={inputCls} required /></Field>
      <Field label="Category">
        <select name="category" className={inputCls} defaultValue="general">
          <option value="general">General</option>
          <option value="billing">Billing</option>
          <option value="data">Data / GIS</option>
          <option value="technical">Technical</option>
        </select>
      </Field>
      <div className="md:col-span-2">
        <Field label="Message"><textarea name="message" rows={4} className={`${inputCls} min-h-[96px] resize-y`} required /></Field>
      </div>
      <div className="md:col-span-2 border border-eds-border rounded-eds-md p-3 space-y-2">
        <label className="flex items-center gap-2 text-sm text-eds-fg">
          <input type="checkbox" checked={whatsappOptIn} onChange={(e) => setWhatsappOptIn(e.target.checked)} />
          <MessageCircle className="w-4 h-4 text-eds-emerald-2" />
          Allow GIISDEC to reply via WhatsApp
        </label>
        {whatsappOptIn && (
          <Field label="WhatsApp number"><input name="whatsapp" className={inputCls} placeholder="+233…" required /></Field>
        )}
      </div>
      <div className="md:col-span-2 flex items-center gap-3">
        <Button className={submitCls} type="submit" disabled={busy}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Submit enquiry
        </Button>
        {done && <span className="inline-flex items-center gap-1 text-sm text-eds-emerald-2"><CheckCircle2 className="w-4 h-4" /> Submitted via geo-api.</span>}
      </div>
      {error && (
        <div role="alert" className="md:col-span-2 text-sm text-eds-danger bg-eds-danger/8 border border-eds-danger/30 rounded-eds-md px-3 py-2">
          {error}
        </div>
      )}
    </form>
  );
};

/* ───────── Notification preferences ───────── */

export const NotificationPreferencesPanel: React.FC = () => {
  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { notificationService.getPreferences().then(setPrefs); }, []);

  if (!prefs) return <Loading label="Loading notification preferences…" />;

  const update = (patch: Partial<NotificationPreferences>) => setPrefs({ ...prefs, ...patch });

  const onSave = async () => {
    setSaving(true);
    setSaved(false);
    setSaveError(null);

    try {
      const result = await notificationService.updatePreferences(prefs);

      if (result.error) {
        setSaveError(
          result.status === 403
            ? 'You do not currently have permission to update notification preferences.'
            : 'Notification preferences could not be saved right now. Please try again.'
        );
        return;
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  const Row: React.FC<{ label: string; desc: string; field: keyof NotificationPreferences }> = ({ label, desc, field }) => (
    <div className="flex items-start justify-between gap-4 py-3 border-t border-eds-border first:border-t-0">
      <div>
        <div className="text-sm font-medium text-eds-fg">{label}</div>
        <div className="text-sm font-medium text-eds-muted-fg">{desc}</div>
      </div>
      <label className="inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          className="sr-only peer"
          checked={Boolean(prefs[field])}
          onChange={(e) => update({ [field]: e.target.checked } as Partial<NotificationPreferences>)}
        />
        <div className="w-10 h-5 bg-eds-muted peer-checked:bg-eds-success rounded-eds-pill relative transition">
          <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-eds-surface rounded-eds-pill transition peer-checked:translate-x-5" />
        </div>
      </label>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="border border-eds-border rounded-eds-md p-4">
        <Row label="Email"               desc="System notifications & receipts."          field="email" />
        <Row label="SMS"                 desc="Short workflow alerts."                    field="sms" />
        <Row label="WhatsApp"            desc="Conversational updates & support replies." field="whatsapp" />
        {prefs.whatsapp && (
          <div className="pt-3">
            <Field label="WhatsApp number">
              <input
                className={inputCls}
                value={prefs.whatsappNumber || ''}
                onChange={(e) => update({ whatsappNumber: e.target.value })}
                placeholder="+233…"
              />
            </Field>
          </div>
        )}
        <Row label="Renewal reminders"   desc="Permit / license renewals."                field="renewalReminders" />
        <Row label="Payment reminders"   desc="Upcoming invoices & subscriptions."        field="paymentReminders" />
      </div>
      <div className="flex items-center gap-3">
        <Button className={submitCls} onClick={onSave} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Save preferences
        </Button>
        {saved && <span className="text-sm text-eds-emerald-2">Saved.</span>}
        {saveError && (
          <span role="alert" className="text-sm text-eds-danger">
            {saveError}
          </span>
        )}
      </div>
    </div>
  );
};

/* ───────── Notification history ───────── */

export const NotificationHistoryPanel: React.FC<{ rows: NotificationItem[] }> = ({ rows }) => (
  <div>
    {rows.length === 0 ? (
      <EmptyState
        icon={<Bell className="w-5 h-5" />}
        title="No notifications yet"
        message="Workflow, agreement, payment and system updates will appear here as they occur."
      />
    ) : (
      <ul className="divide-y divide-eds-border border border-eds-border rounded-eds-md">
        {rows.map((n) => (
          <li key={n.id} className={`p-3 ${n.read ? '' : 'bg-eds-success/10'}`}>
            <div className="flex items-center justify-between">
              <span className="font-medium text-eds-fg text-sm">{n.title}</span>
              <span className="text-xs text-eds-muted-fg">{new Date(n.createdAt).toLocaleString()}</span>
            </div>
            <p className="text-sm text-eds-muted-fg mt-0.5">{n.body}</p>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default {
  SupportEnquiryForm,
  NotificationPreferencesPanel,
  NotificationHistoryPanel,
};
